import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

/* ===== API base (as requested) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper (local to this file) */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

const api = {
  listPrompts: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/prompts${qs}`);
  },
  getPrompt: (id) => http(`/api/prompts/${id}`),
  createPrompt: (payload) => http(`/api/prompts`, { method: "POST", data: payload }),
  updatePrompt: (id, payload) => http(`/api/prompts/${id}`, { method: "PATCH", data: payload }),
  deletePrompt: (id) => http(`/api/prompts/${id}`, { method: "DELETE" }),
  trackRead: (id) => http(`/api/prompts/${id}/track-read`, { method: "POST" }),

  listCategories: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/categories${qs}`);
  },
  listTags: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/tags${qs}`);
  },
};

/* ===== Utilities ===== */
function useLockBodyScroll(locked) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (locked) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [locked]);
}

/* Theme */
const theme = {
  brand: "#3dccc7",
  accent: "#ff914d",
  white: "#f1f1f1",
  subtext: "#d6d9de",
  card: "#0f1b2d",
  border: "#1e293b",
  bg: "#0b1320",
};

const Wrap = styled.div`display:grid;gap:16px;`;
const TitleRow = styled.div`display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;`;
const Title = styled.h2`margin:0;font-size:clamp(18px,2.4vw,22px);font-weight:800;color:${theme.white};`;
const AddBtn = styled.button`
  display:inline-flex;align-items:center;gap:8px;padding:10px 14px;border-radius:10px;
  border:1px solid ${theme.border};background:${theme.accent};color:#111;font-weight:800;cursor:pointer;
  &:hover{filter:brightness(1.05);}
`;

const TableShell = styled.div`
  border:1px solid ${theme.border};
  background:linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01));
  border-radius:16px;overflow:hidden;
`;
const Scroll = styled.div`max-width:100%;overflow:auto;`;
const Table = styled.table`width:100%;border-collapse:separate;border-spacing:0;min-width:1500px;`;
const Thead = styled.thead`background:linear-gradient(180deg,#0f1b2d,#0b1626);position:sticky;top:0;z-index:1;`;
const Th = styled.th`
  text-align:left;padding:12px 16px;font-size:12px;color:${theme.subtext};text-transform:uppercase;
  border-bottom:1px solid ${theme.border};white-space:nowrap;
`;
const Tr = styled.tr`
  transition:background 0.12s ease;
  &:nth-child(even){background:rgba(255,255,255,0.02);}
  &:hover{background:rgba(255,255,255,0.05);}
`;
const Td = styled.td`
  padding:14px 16px;font-size:14px;color:${theme.white};border-bottom:1px dashed ${theme.border};vertical-align:top;
`;

const Truncated = styled.div`
  max-width:${p=>p.w || 300}px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;cursor:pointer;color:${theme.accent};
`;

const FilterBox = styled.select`
  margin-top:6px;background:${theme.card};border:1px solid ${theme.border};color:${theme.white};
  font-size:12px;border-radius:6px;padding:4px 8px;outline:none;cursor:pointer;
  option{background:#0b1320;color:${theme.white};}
`;

const Tag = styled.span`
  display:inline-block;margin-right:6px;margin-top:2px;padding:4px 8px;border-radius:8px;
  background:${theme.brand};font-size:12px;font-weight:600;color:${theme.white};
`;

const ActionBtn = styled.button`
  margin-right:8px;padding:6px 10px;border-radius:8px;border:1px solid ${theme.border};
  background:${(p)=> (p.delete ? "#e74c3c" : theme.accent)};color:#111;font-size:12px;font-weight:700;cursor:pointer;
  &:hover{filter:brightness(1.1);}
`;

/* ===== Modal (scrollable, mobile-safe) ===== */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
  padding: 16px;        /* small gutters on small screens */
  z-index: 50;
  overflow: auto;       /* fallback scroll if modal exceeds viewport */
`;
const Modal = styled.div`
  width: min(700px, 92vw);
  max-height: calc(100dvh - 32px);
  border-radius: 16px;
  border: 1px solid ${theme.border};
  background: ${theme.card};
  box-shadow: 0 30px 70px rgba(0,0,0,0.45);
  display: flex;
  flex-direction: column;
  overflow: hidden;     /* keep header/footer fixed; body scrolls */
`;
const ModalHeader = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 16px;border-bottom:1px solid ${theme.border};flex-shrink:0;
`;
const ModalTitle = styled.h3`margin:0;font-size:16px;font-weight:700;color:${theme.white};`;
const CloseBtn = styled.button`
  width:34px;height:34px;display:grid;place-items:center;border-radius:10px;border:1px solid ${theme.border};
  background:rgba(255,255,255,0.06);color:${theme.white};cursor:pointer;&:hover{background:rgba(255,255,255,0.1);}
`;
const ModalBody = styled.div`
  padding:16px;
  flex: 1 1 auto;
  min-height: 0;                 /* crucial for flex scrolling */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;
const ModalFooter = styled.div`
  display:flex;justify-content:flex-end;gap:10px;padding:14px 16px;border-top:1px solid ${theme.border};flex-shrink:0;
`;
const Secondary = styled.button`
  padding:10px 14px;border-radius:10px;border:1px solid ${theme.border};background:rgba(255,255,255,0.06);
  color:${theme.white};cursor:pointer;&:hover{background:rgba(255,255,255,0.1);}
`;
const Primary = styled.button`
  padding:10px 14px;border-radius:10px;border:1px solid ${theme.border};background:${theme.accent};
  color:#111;font-weight:800;cursor:pointer;&:hover{filter:brightness(1.05);}
`;

/* Inputs */
const Label = styled.label`font-size:13px;color:${theme.subtext};display:block;margin-bottom:4px;`;
const Input = styled.input`
  width:100%;background:${theme.bg};color:${theme.white};border:1px solid ${theme.border};border-radius:10px;
  padding:10px 12px;outline:none;&:focus{border-color:${theme.brand};box-shadow:0 0 0 2px rgba(61,114,148,0.25);}
`;
const TextArea = styled.textarea`
  width:100%;min-height:80px;background:${theme.bg};color:${theme.white};border:1px solid ${theme.border};
  border-radius:10px;padding:10px 12px;resize:vertical;outline:none;
  &:focus{border-color:${theme.brand};box-shadow:0 0 0 2px rgba(61,114,148,0.25);}
`;
const Select = styled.select`
  width:100%;background:${theme.bg};color:${theme.white};border:1px solid ${theme.border};border-radius:10px;
  padding:10px 12px;outline:none;option{background:#0b1320;color:${theme.white};}
`;
const RadioGroup = styled.div`
  display:flex;gap:16px;margin-top:4px;color:${theme.white};font-size:14px;input{margin-right:6px;}
`;

/* Dropdown for Tags */
const TagDropdownWrap = styled.div`position:relative;`;
const TagDropdownBtn = styled.div`
  width:100%;background:${theme.bg};color:${theme.white};border:1px solid ${theme.border};
  border-radius:10px;padding:10px 12px;cursor:pointer;user-select:none;
`;
const TagDropdownList = styled.div`
  position:absolute;top:105%;left:0;right:0;background:${theme.card};border:1px solid ${theme.border};
  border-radius:10px;padding:8px;max-height:min(220px, 40vh);overflow-y:auto;z-index:100;
  -webkit-overflow-scrolling: touch;
`;
const TagOption = styled.label`
  display:flex;align-items:center;gap:8px;font-size:13px;color:${theme.white};margin-bottom:6px;cursor:pointer;
`;

/* Tiny Toasts (no deps) */
const ToastWrap = styled.div`
  position:fixed;right:16px;bottom:16px;display:grid;gap:10px;z-index:1000;
`;
const ToastItem = styled.div`
  background:${p => (p.variant === "error" ? "#b91c1c" : "#16a34a")};color:#fff;border-radius:10px;
  padding:10px 14px;border:1px solid ${theme.border};box-shadow:0 10px 30px rgba(0,0,0,0.35);min-width:220px;
`;

export default function Prompts() {
  /* Data */
  const [rows, setRows] = useState([]); // from /api/prompts
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  /* Filters */
  const [filterCat, setFilterCat] = useState("all");

  /* Reference data */
  const [categories, setCategories] = useState([]); // {_id,name,slug}
  const [tags, setTags] = useState([]);             // {_id,name,slug}

  /* Modals and form */
  const [openAdd, setOpenAdd] = useState(false);
  const [editPrompt, setEditPrompt] = useState(null);

  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [detailedDesc, setDetailedDesc] = useState(""); // existing
  const [timeSaved, setTimeSaved] = useState("");       // NEW
  const [prompt, setPrompt] = useState("");
  const [catId, setCatId] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [type, setType] = useState("Free");

  const [viewOpen, setViewOpen] = useState(false);
  const [viewPrompt, setViewPrompt] = useState(null);

  const [tagOpen, setTagOpen] = useState(false);

  /* Toasts */
  const [toasts, setToasts] = useState([]); // {id,msg,variant}
  const pushToast = (msg, variant = "success") => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg, variant }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2800);
  };

  /* Lock body scroll when a modal is open */
  useLockBodyScroll(openAdd || viewOpen);

  /* Helpers */
  const resetForm = () => {
    setTitle("");
    setDesc("");
    setDetailedDesc("");
    setTimeSaved("");           // NEW
    setPrompt("");
    setCatId(categories[0]?._id || "");
    setSelectedTagIds([]);
    setType("Free");
  };

  const toggleTag = (id) => {
    if (selectedTagIds.includes(id)) {
      setSelectedTagIds(selectedTagIds.filter((t) => t !== id));
    } else if (selectedTagIds.length < 3) {
      setSelectedTagIds([...selectedTagIds, id]);
    }
  };

  /* Fetch reference data then prompts */
  const fetchRefs = async () => {
    const [catRes, tagRes] = await Promise.all([
      api.listCategories({ page: 1, pageSize: 200 }),
      api.listTags({ page: 1, pageSize: 300 }),
    ]);
    setCategories(catRes.data || []);
    setTags(tagRes.data || []);
    if (!catId && (catRes.data || []).length) {
      setCatId(catRes.data[0]._id);
    }
  };

  const fetchPrompts = async () => {
    const params = { page: 1, pageSize: 200, sort: "newest" };
    if (filterCat !== "all") params.category = filterCat; // id
    const res = await api.listPrompts(params);
    setRows(res.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchRefs();
        await fetchPrompts();
        setErr("");
      } catch (e) {
        setErr(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetch when filter changes
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchPrompts();
        setErr("");
      } catch (e) {
        setErr(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCat]);

  /* Create / Update */
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!title.trim() || !desc.trim() || !prompt.trim() || !catId) {
        pushToast("Please fill all required fields", "error");
        return;
      }

      const payload = {
        title,
        desc,
        detailedDesc: detailedDesc?.trim() || undefined,
        timeSaved: timeSaved?.trim() || undefined,  // NEW
        prompt,
        categoryId: catId,
        tagIds: selectedTagIds,
        type,
      };

      if (editPrompt) {
        const res = await api.updatePrompt(editPrompt._id, payload);
        setRows((prev) => prev.map((r) => (r._id === editPrompt._id ? res.data : r)));
        pushToast("Prompt updated");
      } else {
        const res = await api.createPrompt(payload);
        setRows((prev) => [res.data, ...prev]);
        pushToast("Prompt created");
      }
      setOpenAdd(false);
      setEditPrompt(null);
      resetForm();
    } catch (e) {
      pushToast(e.message || "Action failed", "error");
    }
  };

  /* Edit / Delete */
  const handleEdit = (r) => {
    setEditPrompt(r);
    setTitle(r.title || "");
    setDesc(r.desc || "");
    setDetailedDesc(r.detailedDesc || "");
    setTimeSaved(r.timeSaved || "");       // NEW
    setPrompt(r.prompt || "");
    setCatId(r?.category?._id || "");
    setSelectedTagIds((r.tags || []).map((t) => t._id));
    setType(r.type || "Free");
    setOpenAdd(true);
    setTagOpen(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this prompt?")) return;
    try {
      await api.deletePrompt(id);
      setRows((prev) => prev.filter((x) => x._id !== id));
      pushToast("Prompt deleted");
    } catch (e) {
      pushToast(e.message || "Delete failed", "error");
    }
  };

  /* View (and track read) */
  const openView = async (r) => {
    setViewPrompt(r);
    setViewOpen(true);
    try {
      const res = await api.trackRead(r._id);
      // update reads locally
      setRows((prev) =>
        prev.map((x) => (x._id === r._id ? { ...x, reads: res.data.reads } : x))
      );
    } catch {
      /* non-blocking */
    }
  };

  const filteredRows = useMemo(() => {
    if (filterCat === "all") return rows;
    return rows.filter((r) => r?.category?._id === filterCat);
  }, [rows, filterCat]);

  return (
    <Wrap>
      <TitleRow>
        <Title>Prompts</Title>
        <AddBtn
          onClick={() => {
            resetForm();
            setEditPrompt(null);
            setOpenAdd(true);
          }}
        >
          + Add New Prompt
        </AddBtn>
      </TitleRow>

      <TableShell>
        <Scroll>
          <Table>
            <Thead>
              <Tr>
                <Th>Id</Th>
                <Th>Title</Th>
                <Th>Description</Th>
                <Th>Detailed Description</Th>
                <Th>Prompt</Th>
                <Th>
                  Category
                  <br />
                  <FilterBox
                    value={filterCat}
                    onChange={(e) => setFilterCat(e.target.value)}
                  >
                    <option value="all">All</option>
                    {categories.map((c) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </FilterBox>
                </Th>
                <Th>Tags</Th>
                <Th>Time Saved</Th> {/* NEW column */}
                <Th>Reads</Th>
                <Th>Type</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <tbody>
              {loading ? (
                <Tr><Td colSpan={11}>Loading...</Td></Tr>
              ) : err ? (
                <Tr><Td colSpan={11} style={{ color: "#ffb4b4" }}>{err}</Td></Tr>
              ) : filteredRows.length === 0 ? (
                <Tr>
                  <Td colSpan={11} style={{ textAlign: "center", padding: "20px", color: theme.subtext }}>
                    No prompts available
                  </Td>
                </Tr>
              ) : (
                filteredRows.map((r, index) => (
                  <Tr key={r._id}>
                    <Td>{index + 1}</Td>
                    <Td>{r.title}</Td>
                    <Td>{r.desc}</Td>
                    <Td>
                      <Truncated w={320} onClick={() => openView(r)}>
                        {r.detailedDesc || "—"}
                      </Truncated>
                    </Td>
                    <Td>
                      <Truncated onClick={() => openView(r)}>
                        {r.prompt}
                      </Truncated>
                    </Td>
                    <Td>{r?.category?.name || "-"}</Td>
                    <Td>
                      {(r.tags || []).map((t) => <Tag key={t._id}>{t.name}</Tag>)}
                    </Td>
                    <Td>{r.timeSaved || "—"}</Td> {/* NEW */}
                    <Td>{r.reads ?? 0}</Td>
                    <Td>{r.type}</Td>
                    <Td>
                      <ActionBtn onClick={() => handleEdit(r)}>Edit</ActionBtn>
                      <ActionBtn delete onClick={() => handleDelete(r._id)}>Delete</ActionBtn>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </Table>
        </Scroll>
      </TableShell>

      {/* Add/Edit Prompt Modal */}
      {openAdd && (
        <Overlay>
          <Modal>
            <ModalHeader>
              <ModalTitle>{editPrompt ? "Edit Prompt" : "Add New Prompt"}</ModalTitle>
              <CloseBtn onClick={() => setOpenAdd(false)}>✕</CloseBtn>
            </ModalHeader>

            <form
              onSubmit={onSubmit}
              style={{ display:"flex", flexDirection:"column", flex:1, minHeight: 0 }} // key: minHeight 0
            >
              <ModalBody>
                <div>
                  <Label>Prompt Title</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={desc} onChange={(e) => setDesc(e.target.value)} required />
                </div>
                <div>
                  <Label>Detailed Description</Label>
                  <TextArea
                    value={detailedDesc}
                    onChange={(e) => setDetailedDesc(e.target.value)}
                    placeholder="Add context, usage notes, variables, expected output, examples, etc."
                  />
                </div>
                <div>
                  <Label>Time Saved (e.g., “30 min/task”, “2 hrs/report”)</Label>
                  <Input
                    value={timeSaved}
                    onChange={(e) => setTimeSaved(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label>Actual Prompt</Label>
                  <TextArea value={prompt} onChange={(e) => setPrompt(e.target.value)} required />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={catId} onChange={(e) => setCatId(e.target.value)} required>
                    {categories.map((c) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>Tags (max 3)</Label>
                  <TagDropdownWrap>
                    <TagDropdownBtn onClick={() => setTagOpen(!tagOpen)}>
                      {selectedTagIds.length > 0
                        ? tags.filter(t => selectedTagIds.includes(t._id)).map(t => t.name).join(", ")
                        : "Select tags"}
                    </TagDropdownBtn>
                    {tagOpen && (
                      <TagDropdownList>
                        {tags.map((t) => (
                          <TagOption key={t._id} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedTagIds.includes(t._id)}
                              onChange={() => toggleTag(t._id)}
                            />
                            {t.name}
                          </TagOption>
                        ))}
                      </TagDropdownList>
                    )}
                  </TagDropdownWrap>
                </div>
                <div>
                  <Label>Type</Label>
                  <RadioGroup>
                    <label>
                      <input type="radio" value="Free" checked={type === "Free"} onChange={() => setType("Free")} />
                      Free
                    </label>
                    <label>
                      <input type="radio" value="Paid" checked={type === "Paid"} onChange={() => setType("Paid")} />
                      Paid
                    </label>
                  </RadioGroup>
                </div>
              </ModalBody>

              <ModalFooter>
                <Secondary type="button" onClick={() => setOpenAdd(false)}>Close</Secondary>
                <Primary type="submit">Submit</Primary>
              </ModalFooter>
            </form>
          </Modal>
        </Overlay>
      )}

      {/* View Prompt Modal */}
      {viewOpen && viewPrompt && (
        <Overlay>
          <Modal>
            <ModalHeader>
              <ModalTitle>{viewPrompt.title}</ModalTitle>
              <CloseBtn onClick={() => setViewOpen(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <div style={{ color: theme.white, fontSize: 14, lineHeight: 1.6 }}>
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ color: theme.subtext }}>Description:</strong>
                  <div>{viewPrompt.desc || "—"}</div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ color: theme.subtext }}>Detailed Description:</strong>
                  <div style={{ whiteSpace: "pre-wrap" }}>{viewPrompt.detailedDesc || "—"}</div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ color: theme.subtext }}>Time Saved:</strong>
                  <div>{viewPrompt.timeSaved || "—"}</div>
                </div>
                <div>
                  <strong style={{ color: theme.subtext }}>Prompt:</strong>
                  <p style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>{viewPrompt.prompt}</p>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Secondary onClick={() => setViewOpen(false)}>Close</Secondary>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {/* Toasts */}
      <ToastWrap>
        {toasts.map((t) => (
          <ToastItem key={t.id} variant={t.variant}>{t.msg}</ToastItem>
        ))}
      </ToastWrap>
    </Wrap>
  );
}
